import { STATUS_RAM } from "@/constants/STATUS_RAM";
import { TIPO_INSPECAO } from "@/constants/TIPO_INSPECAO";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { usePermissao } from "@/hooks/usePermissao";
import { useRelatorioAM } from "@/hooks/useRelatorioAM";
import { IListaRelatorioAM } from "@/interfaces/IListaRelatorioAM";
import { IRelatorioAM } from "@/interfaces/IRelatorioAM";
import { ImpresssaoRelatorioAM } from "@/pages/relatorioAM/_components/ImpresssaoRelatorioAM";
import { ModalCancelarRAM } from "@/pages/relatorioAM/_components/ModalCancelarRAM";
import { formatMessageError } from "@/utils/formatMessageError";
import {
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { App, Button, List, Space, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";

export function ItemListFiltroRelatorioAM({
  item,
  reloadItems,
}: {
  item: IListaRelatorioAM["content"][number];
  reloadItems: () => void;
}) {
  const { isOnline } = useNetworkStatus();
  const relatorioRef = useRef<HTMLDivElement>(null);
  const {
    getRelatorioAMByIdApi,
    addRelatorioAM,
    listaRelatorioAM,
    getAnexoApi,
    canCancelRelatorio,
    canViewAndPrintRelatorio,
    validateFinalizadoRelatorio,
    canEditRelatorio,
  } = useRelatorioAM();
  const { hasEnabledPermissao } = usePermissao();
  const { notification, message } = App.useApp();
  const navigate = useNavigate();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [isOpenModalCancelar, setIsOpenModalCancelar] = useState(false);
  const [relatorio, setRelatorio] = useState<IRelatorioAM | null>(null);

  const redirectToView = () => {
    navigate(`/RAM/visualizar/${item.id}`);
  };

  const handlePrint = useReactToPrint({
    contentRef: relatorioRef,
    documentTitle: "relatorio_am",
    suppressErrors: true,
    preserveAfterPrint: true,
  });

  const convertBlobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          resolve(reader.result as string);
        } else {
          reject(new Error("Erro ao converter blob para base64"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const redirectToEdit = async () => {
    const relatorioOffline = listaRelatorioAM.find((r) => r.id === item.id);

    if (relatorioOffline && relatorioOffline.situacao === "PENDENTE") {
      navigate(`/RAM/lista/editar/${relatorioOffline.offlineId}`);
      return;
    }

    if (relatorioOffline && relatorioOffline.situacao !== "PENDENTE") {
      notification.warning({
        message: "Relatório de Auditoria e Monitoria finalizado",
        description:
          "Não é possível editar um relatório que já foi finalizado.",
        key: "warning-finalizado",
      });
      return;
    }

    const relatorioOnline = await getRelatorioAMByIdApi(item.id!);

    if (relatorioOnline && relatorioOnline.situacao === "PENDENTE") {
      // Processa os itens do relatório para baixar anexos e converter para base64
      const itensComAnexosBase64 = await Promise.all(
        relatorioOnline.itens.map(async (itemRelatorio) => {
          if (!itemRelatorio.anexos || itemRelatorio.anexos.length === 0) {
            return itemRelatorio;
          }

          const anexosComBase64 = await Promise.all(
            itemRelatorio.anexos.map(async (anexo) => {
              try {
                // Se o anexo já tem base64, mantém ele
                if (anexo.arquivoBase64) {
                  return anexo;
                }

                // Se existe arquivo online, busca o blob e converte para base64
                if (anexo.nomeUpload) {
                  const blobAnexo = await getAnexoApi(anexo.nomeUpload);
                  const base64 = await convertBlobToBase64(blobAnexo);

                  return {
                    ...anexo,
                    arquivoBase64: base64,
                    arquivo: undefined, // Remove o arquivo para uso offline
                  };
                }

                return anexo;
              } catch (error) {
                console.warn(
                  `Erro ao baixar anexo ${anexo.nomeArquivo}:`,
                  error
                );
                message.warning({
                  content: `Não foi possível baixar o anexo ${anexo.nomeArquivo}.`,
                  key: `warning-anexo-${anexo.nomeArquivo}`,
                });

                return {
                  ...anexo,
                  arquivoBase64: undefined,
                  arquivo: undefined,
                };
              }
            })
          );

          return {
            ...itemRelatorio,
            anexos: anexosComBase64,
          };
        })
      );

      const relatorioSynced: IRelatorioAM = {
        ...relatorioOnline,
        itens: itensComAnexosBase64,
        isUpdated: true,
        offlineId: Date.now(), // Gera um offlineId unico
      };

      addRelatorioAM(relatorioSynced);

      navigate(`/RAM/lista/editar/${relatorioSynced?.offlineId}`);
      return;
    }

    if (relatorioOnline && relatorioOnline.situacao !== "PENDENTE") {
      notification.warning({
        message: "Relatório de Auditoria e Monitoria finalizado",
        description:
          "Não é possível editar um relatório que já foi finalizado.",
        key: "warning-finalizado",
      });
      return;
    }
  };

  const handleDownload = async () => {
    try {
      if (listaRelatorioAM.find((relatorio) => relatorio.id === item.id)) {
        notification.info({
          message:
            "Este Relatório de Auditoria e Monitoria já foi baixado localmente.",
          key: "info",
        });
        return;
      }

      message.loading({
        content: "Baixando relatório de auditoria e monitoria...",
        key: "loading",
        duration: 0,
      });
      setIsLoadingDownload(true);

      const relatorio = await getRelatorioAMByIdApi(item.id!);
      // Processa os itens do relatório para baixar anexos e converter para base64
      const itensComAnexosBase64 = await Promise.all(
        relatorio.itens.map(async (itemRelatorio) => {
          if (!itemRelatorio.anexos || itemRelatorio.anexos.length === 0) {
            return itemRelatorio;
          }

          const anexosComBase64 = await Promise.all(
            itemRelatorio.anexos.map(async (anexo) => {
              try {
                // Se o anexo já tem base64, mantém ele
                if (anexo.arquivoBase64) {
                  return anexo;
                }

                // Se existe arquivo online, busca o blob e converte para base64
                if (anexo.nomeUpload) {
                  const blobAnexo = await getAnexoApi(anexo.nomeUpload);
                  const base64 = await convertBlobToBase64(blobAnexo);

                  return {
                    ...anexo,
                    arquivoBase64: base64,
                    arquivo: undefined, // Remove o arquivo para uso offline
                  };
                }

                return anexo;
              } catch (error) {
                console.warn(
                  `Erro ao baixar anexo ${anexo.nomeArquivo}:`,
                  error
                );
                message.warning({
                  content: `Não foi possível baixar o anexo ${anexo.nomeArquivo}.`,
                  key: `warning-anexo-${anexo.nomeArquivo}`,
                });

                return {
                  ...anexo,
                  arquivoBase64: undefined,
                  arquivo: undefined,
                };
              }
            })
          );

          return {
            ...itemRelatorio,
            anexos: anexosComBase64,
          };
        })
      );

      const relatorioSynced: IRelatorioAM = {
        ...relatorio,
        itens: itensComAnexosBase64,
        isUpdated: true,
      };

      setIsLoadingDownload(false);
      message.destroy("loading");

      addRelatorioAM(relatorioSynced);

      notification.success({
        message: "Relatório de auditoria e monitoria baixado com sucesso!",
        key: "success",
      });
    } catch (error) {
      console.error(
        "Erro ao baixar o relatório de auditoria e monitoria:",
        error
      );
      message.destroy("loading");
      setIsLoadingDownload(false);
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível baixar o relatório de auditoria e monitoria",
        key: "error",
        description: messageError,
      });
    }
  };

  const handleOpenModalCancelar = () => {
    setIsOpenModalCancelar(true);
  };

  const handleMountRelatorio = async () => {
    const relatorio = await getRelatorioAMByIdApi(item.id!);
    setRelatorio(relatorio);
  };

  useEffect(() => {
    if (relatorio) {
      handlePrint();
    }
  }, [relatorio, handlePrint]);

  const enabledCancelAction = canCancelRelatorio(
    item.formulario.tipoFormulario,
    item.estabelecimento.id
  );

  const enabledViewAndPrintAction = canViewAndPrintRelatorio(
    item.formulario.classificacao,
    item.estabelecimento.id
  );

  const enabledEditAction = canEditRelatorio(item.estabelecimento.id);

  return (
    <>
      <List.Item
        extra={
          <Space>
            <Typography.Text type="secondary">
              {STATUS_RAM[item.situacao]}
            </Typography.Text>

            <Tooltip title="Baixar Para uso de campo" destroyOnHidden>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                disabled={
                  isLoadingDownload ||
                  !isOnline ||
                  item.situacao === "CANCELADO"
                }
                loading={isLoadingDownload}
              />
            </Tooltip>

            {enabledViewAndPrintAction && (
              <Tooltip title="Imprimir" destroyOnHidden>
                <Button
                  size="small"
                  icon={<PrinterOutlined />}
                  onClick={handleMountRelatorio}
                  disabled={!isOnline || item.situacao === "CANCELADO"}
                />
              </Tooltip>
            )}

            {enabledEditAction && (
              <Tooltip title="Editar" destroyOnHidden>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={redirectToEdit}
                  disabled={
                    !isOnline ||
                    item.situacao === "CANCELADO" ||
                    !hasEnabledPermissao("RAM_EDITAR")
                  }
                />
              </Tooltip>
            )}

            {enabledViewAndPrintAction && (
              <Tooltip title="Visualizar" destroyOnHidden>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={redirectToView}
                  disabled={
                    !isOnline ||
                    item.situacao === "CANCELADO" ||
                    !hasEnabledPermissao("RAM_VISUALIZAR")
                  }
                />
              </Tooltip>
            )}

            {enabledCancelAction && (
              <Tooltip title="Cancelar" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={handleOpenModalCancelar}
                  disabled={
                    !isOnline ||
                    item.situacao === "CANCELADO" ||
                    !hasEnabledPermissao("RAM_CANCELAR")
                  }
                />
              </Tooltip>
            )}
          </Space>
        }
      >
        <List.Item.Meta
          title={
            <Typography.Text
              strong
              style={{
                color: isOpenModalCancelar ? "#d7263d" : undefined,
                opacity: item.situacao === "CANCELADO" ? 0.6 : 1,
              }}
            >
              RAM {item.id}
            </Typography.Text>
          }
          description={
            <Typography.Text
              style={{
                color: isOpenModalCancelar ? "#d7263d" : "#8c8c8c",
                fontSize: 12,
              }}
            >
              Estabelecimento: {item.estabelecimento.numeroInspecao}-
              {TIPO_INSPECAO[item.estabelecimento.tipoInspecao]}-
              {item.estabelecimento.nome} <br />
              Data Preenchimento:{" "}
              {item.dataMonitoria
                ? dayjs(item.dataMonitoria).format("DD/MM/YYYY")
                : "-"}
            </Typography.Text>
          }
        />
      </List.Item>

      <div style={{ display: "none" }}>
        <ImpresssaoRelatorioAM
          marcadagua=""
          ref={relatorioRef}
          item={relatorio}
        />
      </div>

      <ModalCancelarRAM
        ramId={item.id!}
        isOpen={isOpenModalCancelar}
        onClose={() => {
          setIsOpenModalCancelar(false);
        }}
        reloadItems={reloadItems}
      />
    </>
  );
}
